/* The customer app: sign in, show the code, see the cards.
 *
 * REBUILT ON 2026-09-17 FROM `Damga Customer App.dc.html`, the light register:
 * four tabs, a header with the code one tap away, sheets, a toast and the seal.
 * What the artboard draws and the product does not do is cut, not softened, and
 * each cut is written where it would have been. The decisions, with what each
 * rests on, are in reports/surfaces/customer-app.md.
 *
 * NO SAMPLE DATA, ANYWHERE, EVER.
 * An empty wallet renders its real copy. It does not render a placeholder card,
 * a skeleton that never resolves, or a tour. Day one is the state this product
 * will be in for months and it is a designed state, not a gap to paper over.
 *
 * EVERYTHING BELOW THE RENDER IS THE LOGIC THIS APP ALREADY HAD, kept: the one
 * cold-start call, the clock correction, the bounded refresh after a code is
 * shown, the provider round trip, and the passkey offer after a code.
 */
import {
  Offline,
  SessionExpired,
  type Session,
  createAuth,
  createClient,
  isRefusal,
  localStorageSecretStore,
  readSession,
  t,
  writeSession,
  registerPasskey,
  passkeySupport,
  readPasskeyEnrolled,
  writePasskeyEnrolled,
  beginOAuth,
  completeOAuth,
  enabledProviders,
  looksLikeOAuthReturn,
  type OAuthProvider,
} from "@damga/kit";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { API_URL, AUTH_URL, OTP_CHANNEL, SIGN_IN_METHODS } from "./config";
import { Activity, type Entry } from "./screens/Activity";
import { Archive } from "./screens/Archive";
import { CardDetail } from "./screens/CardDetail";
import { Home } from "./screens/Home";
import { Profile } from "./screens/Profile";
import { Settings } from "./screens/Settings";
import { SignIn } from "./screens/SignIn";
import { UsePasskey } from "./screens/UsePasskey";
import { Wallet, type WalletView } from "./screens/Wallet";
import { Welcome, type AuthMode, type SignInMethod } from "./screens/Welcome";
import {
  CodeSheet,
  EmailSheet,
  EraseSheet,
  ExportSheet,
  NameSheet,
  RedeemSheet,
  SignOutSheet,
  UpdatesSheet,
  WhoSeesSheet,
} from "./sheets";
import { useFavourites } from "./favourites";
import { noticesFor, readSeen, writeSeen } from "./notices";
import { Brand, Icon, Intro, Seal, Toast, phoneForDisplay } from "./ui";

export type Card = {
  membership_id: string;
  business_id: string;
  business_name: string;
  category: string | null;
  program_type: string;
  tier_name: string;
  threshold: number;
  reward_description: string;
  balance: number;
  claimable: number;
  /** When the soonest claimable reward lapses, or null if none is claimable. */
  reward_expires_at: string | null;
  last_earn_at: string | null;
  archived_at: string | null;
  status: "open" | "closing" | "closed";
  closing_started_at: string | null;
  claimable_until: string | null;
  closed_at: string | null;
  // Added to the API on 2026-09-17 for the card detail. Optional, because the
  // app can reach a server that predates them, and a fact the server did not
  // send is left off rather than drawn as zero.
  min_gap_minutes?: number | null;
  points_per_stamp?: number | null;
  reward_validity_days?: number | null;
  enrolled_at?: string | null;
  visits?: number;
  rewards_taken?: number;
};

type SessionResponse = {
  profile: {
    user_id: string;
    display_name: string | null;
    phone_e164: string;
    email: string | null;
    email_verified: boolean;
  };
  code: { secret: string; digits: number; period_seconds: number; algorithm: string };
  cards: Card[];
};

// Five, as the artboard draws them. Profile was folded into Settings on
// 2026-09-17 and put back the same day: what was thin about it was the parts
// that were false, not the screen.
type Tab = "home" | "wallet" | "activity" | "profile" | "settings";
const TABS: { key: Tab; icon: string }[] = [
  { key: "home", icon: "home" },
  { key: "wallet", icon: "card" },
  { key: "activity", icon: "chart" },
  { key: "profile", icon: "users" },
  { key: "settings", icon: "gear" },
];
type Detail = { kind: "card"; id: string } | { kind: "archive" } | null;
type SheetState =
  | { kind: "code" }
  | { kind: "redeem"; id: string }
  | { kind: "name" }
  | { kind: "email" }
  | { kind: "who" }
  | { kind: "updates" }
  | { kind: "export" }
  | { kind: "signout" }
  | { kind: "erase" }
  | null;

/* An email is worth asking for once the stamps justify it: a card within this
 * many of its reward. The earlier ruling, kept. */
const NEARLY_THERE = 3;

function greeting(): string {
  const h = new Date().getHours();
  return t(h < 12 ? "shell.eyebrow.morning" : h < 18 ? "shell.eyebrow.afternoon" : "shell.eyebrow.evening");
}

export function App() {
  const store = useMemo(() => localStorageSecretStore(), []);
  const [session, setSessionState] = useState<Session | null>(() => readSession<Session>());
  const [secret, setSecret] = useState(() => store.read());
  const [cards, setCards] = useState<Card[] | null>(null);
  const [entries, setEntries] = useState<Entry[] | null>(null);
  const [feedFailed, setFeedFailed] = useState(false);
  const [problem, setProblem] = useState<"offline" | "server" | null>(null);
  // "We have not asked yet" is not "we asked and there are none". Collapsing
  // those is how an empty wallet ends up showing a spinner forever.
  const [asked, setAsked] = useState(false);
  const [chose, setChose] = useState<SignInMethod["id"] | null>(null);
  const [authMode, setAuthMode] = useState<AuthMode>("signin");
  const [authError, setAuthError] = useState<string | null>(null);
  // A provider trip is in flight on this load. Read from the URL synchronously,
  // so somebody back from Google never sees the sign-in screen for a frame.
  const [returning, setReturning] = useState(() =>
    typeof window === "undefined" ? false : looksLikeOAuthReturn(window.location),
  );
  // `null` is "not told yet", which must not render as "none".
  const [liveProviders, setLiveProviders] = useState<Set<OAuthProvider> | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [emailVerified, setEmailVerified] = useState(false);
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [phone, setPhone] = useState("");

  // The passkey offer. "Has this customer a passkey" is really "has this DEVICE
  // one", so the flag lives on the device.
  const [offerPasskey, setOfferPasskey] = useState(false);
  const [hasPasskey, setHasPasskey] = useState(() => readPasskeyEnrolled());
  const [canPasskey, setCanPasskey] = useState(false);

  // The shell.
  const [tab, setTab] = useState<Tab>("home");
  const [detail, setDetail] = useState<Detail>(null);
  const [dir, setDir] = useState<"forward" | "back">("forward");
  const [sheet, setSheet] = useState<SheetState>(null);
  const [toast, setToast] = useState<{ message: string; stamp: number } | null>(null);
  const [seal, setSeal] = useState<string | null>(null);
  const [landed, setLanded] = useState<{ id: string; index: number } | null>(null);
  const [erased, setErased] = useState(false);
  const [walletView, setWalletView] = useState<WalletView>("mine");
  const favourites = useFavourites();
  const [seen, setSeen] = useState<string[]>(() => readSeen());

  useEffect(() => {
    void passkeySupport().then((got) => setCanPasskey(got.platformAuthenticator));
  }, []);

  const toastTimer = useRef<number | undefined>(undefined);
  const say = useCallback((message: string) => {
    window.clearTimeout(toastTimer.current);
    setToast({ message, stamp: Date.now() });
    toastTimer.current = window.setTimeout(() => setToast(null), 3200);
  }, []);
  const sealTimer = useRef<number | undefined>(undefined);
  const press = useCallback((label: string) => {
    window.clearTimeout(sealTimer.current);
    setSeal(label);
    sealTimer.current = window.setTimeout(() => setSeal(null), 1200);
  }, []);

  const setSession = useCallback((s: Session | null) => {
    writeSession(s);
    setSessionState(s);
  }, []);

  const sessionRef = useRef(session);
  sessionRef.current = session;

  const api = useMemo(
    () =>
      createClient({
        apiUrl: API_URL,
        authUrl: AUTH_URL,
        getSession: () => sessionRef.current,
        setSession,
      }),
    [setSession],
  );
  const auth = useMemo(() => createAuth(AUTH_URL, OTP_CHANNEL), []);

  const enrolPasskeyNow = useCallback(async () => {
    const token = sessionRef.current?.access_token;
    if (!token) throw new Error("no session");
    // The passkey endpoints, not the MFA factor ones: a factor binds to a
    // session that already exists and cannot sign anybody in.
    await registerPasskey({
      options: () => auth.passkeyRegistrationOptions(token),
      verify: (challengeId, credential) => auth.passkeyRegistrationVerify(token, challengeId, credential),
    });
    writePasskeyEnrolled(true);
    setHasPasskey(true);
  }, [auth]);

  const loadFeed = useCallback(async () => {
    setFeedFailed(false);
    try {
      const res = await api.call<{ entries: Entry[] }>("/me/activity");
      if (isRefusal(res)) setFeedFailed(true);
      else setEntries((res as { entries: Entry[] }).entries);
    } catch {
      // Swallowing this left the screen on "Loading" for good.
      setFeedFailed(true);
    }
  }, [api]);

  /* A STAMP THAT LANDS WHILE THE APP IS OPEN IS THE PRODUCT.
   *
   * Every refresh is compared with the cards the customer was already looking
   * at. A card that gained a visit, or a reward that became claimable, presses
   * the seal and says which shop and where the card now stands. The first load
   * never does: arriving at cards is not an event. */
  const previous = useRef<Card[] | null>(null);
  const takeCards = useCallback(
    (next: Card[], announce: boolean) => {
      const before = previous.current;
      previous.current = next;
      setCards(next);
      if (!announce || !before) return;
      const old = new Map(before.map((c) => [c.membership_id, c]));
      for (const c of next) {
        const was = old.get(c.membership_id);
        if (was && c.claimable > was.claimable) {
          press(t("seal.ready"));
          say(t("toast.ready", "en", { shop: c.business_name, reward: c.reward_description }));
          void loadFeed();
          return;
        }
        const gained = !was
          ? true
          : typeof c.visits === "number" && typeof was.visits === "number"
            ? c.visits > was.visits
            : c.balance > was.balance;
        if (gained) {
          press(t("seal.stamped"));
          say(
            t("toast.stamped", "en", {
              shop: c.business_name,
              progress: t("wallet.progress", "en", { balance: c.balance, threshold: c.threshold }),
            }),
          );
          if (c.balance > 0) setLanded({ id: c.membership_id, index: Math.min(c.balance, c.threshold) - 1 });
          void loadFeed();
          return;
        }
      }
    },
    [loadFeed, press, say],
  );

  const start = useCallback(async () => {
    if (!sessionRef.current) return;
    try {
      const res = await api.call<SessionResponse>("/me/session", { method: "POST" });
      setAsked(true);
      if (isRefusal(res)) {
        setProblem("server");
        return;
      }
      setProblem(null);
      takeCards(res.cards, false);
      setEmail(res.profile.email);
      setEmailVerified(res.profile.email_verified);
      setDisplayName(res.profile.display_name);
      setPhone(res.profile.phone_e164);
      // The feed is its own call: a cold launch on mobile data should not wait
      // for a ledger it may never open.
      void loadFeed();
      // The gap between this device's clock and the server's, stored with the
      // secret so the code is derived from corrected time. A phone with a wrong
      // clock is otherwise unstampable and finds out at a counter.
      const serverMs = (res.code as { server_time_ms?: number }).server_time_ms;
      const stored = {
        ...res.code,
        userId: res.profile.user_id,
        skewMs: typeof serverMs === "number" ? serverMs - Date.now() : 0,
      };
      store.write(stored);
      setSecret(stored);
    } catch (err) {
      setAsked(true);
      if (err instanceof SessionExpired) {
        setSession(null);
        store.clear();
        setSecret(null);
      } else if (err instanceof Offline) {
        // Not an error state. The code works with no connection, which is the
        // whole design of the identifier.
        setProblem("offline");
      } else {
        setProblem("server");
      }
    }
  }, [api, loadFeed, setSession, store, takeCards]);

  /* THE CARD HAS TO CHANGE WHILE THEY ARE LOOKING AT IT, AND ONLY THEN.
   *
   * Bounded, not ambient: the phone is not subscribing to anything, it is
   * awaiting the result of showing a code to a cashier seconds ago. So a burst
   * runs while an answer is plausible and then stops. The refetch on resume
   * stays, pageshow as well as visibilitychange because iOS restores from the
   * back-forward cache without the latter. */
  const BURST_MS = 25_000;
  const BURST_EVERY_MS = 2_000;
  const pollUntil = useRef(0);
  const [refreshing, setRefreshing] = useState(false);

  const refreshCards = useCallback(async () => {
    if (!sessionRef.current) return;
    try {
      const res = await api.call<{ cards: Card[] }>("/me/cards");
      if (isRefusal(res)) return;
      takeCards(res.cards, true);
      setProblem(null);
    } catch (err) {
      // A failed background refresh is not worth a screen.
      if (err instanceof SessionExpired) {
        setSession(null);
        store.clear();
        setSecret(null);
      }
    }
  }, [api, setSession, store, takeCards]);

  const refreshNow = useCallback(async () => {
    setRefreshing(true);
    try {
      await refreshCards();
    } finally {
      setRefreshing(false);
    }
  }, [refreshCards]);

  useEffect(() => {
    if (!session) return;
    let timer: number | undefined;
    const stop = () => {
      if (timer !== undefined) {
        clearInterval(timer);
        timer = undefined;
      }
    };
    const burst = () => {
      pollUntil.current = Date.now() + BURST_MS;
      if (timer !== undefined) return;
      timer = window.setInterval(() => {
        if (Date.now() > pollUntil.current || document.visibilityState !== "visible") {
          stop();
          return;
        }
        void refreshCards();
      }, BURST_EVERY_MS);
    };
    const onWake = () => {
      if (document.visibilityState !== "visible") {
        stop();
        return;
      }
      void refreshCards();
      burst();
    };
    onWake();
    document.addEventListener("visibilitychange", onWake);
    window.addEventListener("pageshow", onWake);
    window.addEventListener("focus", onWake);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onWake);
      window.removeEventListener("pageshow", onWake);
      window.removeEventListener("focus", onWake);
    };
  }, [session, refreshCards]);

  /* Showing the code IS the request. Home carries the code, and so do the code
   * and redeem sheets, so opening any of them starts the window again. */
  const showingCode = (tab === "home" && !detail) || sheet?.kind === "code" || sheet?.kind === "redeem";
  useEffect(() => {
    if (session && showingCode) pollUntil.current = Date.now() + BURST_MS;
  }, [session, showingCode]);

  useEffect(() => {
    void start();
    // `session` is a dependency so arriving at a session is an event: without
    // it every first sign-in landed on "Loading" and stayed there.
  }, [start, session]);

  const signOut = useCallback(() => {
    setSession(null);
    store.clear();
    setSecret(null);
    setCards(null);
    previous.current = null;
    setEntries(null);
    setFeedFailed(false);
    setAsked(false);
    setEmail(null);
    setEmailVerified(false);
    setDisplayName(null);
    setPhone("");
    setTab("home");
    setDetail(null);
    setSheet(null);
    setChose(null);
  }, [setSession, store]);

  const saveEmail = useCallback(
    async (address: string) => {
      // Not caught here: the sheet keeps the customer's typing when this throws.
      await api.call("/me/profile", { method: "PATCH", body: { email: address } });
      setEmail(address);
      setSheet(null);
      say(t("toast.emailSaved"));
    },
    [api, say],
  );

  const saveName = useCallback(
    async (display_name: string) => {
      await api.call("/me/profile", { method: "PATCH", body: { display_name } });
      setDisplayName(display_name);
      setSheet(null);
      press(t("seal.saved"));
      say(t("toast.nameSaved"));
    },
    [api, press, say],
  );

  /* The file is made when the button is pressed, by the route, and handed to
   * the browser. No email, no wait, and nothing written to a server we would
   * then have to delete. */
  const downloadData = useCallback(async () => {
    const doc = await api.call<Record<string, unknown>>("/me/export");
    if (isRefusal(doc)) throw new Error("refused");
    const url = URL.createObjectURL(new Blob([JSON.stringify(doc, null, 2)], { type: "application/json" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `damga-your-data-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.append(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    setSheet(null);
  }, [api]);

  const erase = useCallback(async () => {
    const res = await api.call<{ refusal?: string }>("/me", { method: "DELETE" });
    if (isRefusal(res)) throw new Error(String((res as { refusal?: string }).refusal));
    signOut();
    setErased(true);
  }, [api, signOut]);

  /* Ask the auth service which provider routes exist, rather than trusting a
   * list here: the copy that drifts is the one that puts a customer on
   * somebody else's error page. */
  useEffect(() => {
    if (session) return;
    let live = true;
    void enabledProviders(AUTH_URL)
      .then((s) => live && setLiveProviders(s))
      // If we cannot ask, show the routes that depend on nobody: a hidden
      // button rather than a broken one.
      .catch(() => live && setLiveProviders(new Set()));
    return () => {
      live = false;
    };
  }, [session]);

  /* Coming back from a provider. Every outcome is handled and told apart,
   * because the remedy differs: cancelled, lost, failed. */
  useEffect(() => {
    if (!returning) return;
    let live = true;
    void (async () => {
      try {
        const r = await completeOAuth(AUTH_URL);
        if (!live) return;
        if (r?.kind === "session") {
          setSession(r.session);
          setAsked(false);
          setOfferPasskey(true);
        } else if (r?.kind === "error") {
          setAuthError(
            r.message === "verifier_missing"
              ? t("auth.providerLost")
              : /access_denied|cancel/i.test(r.message)
                ? t("auth.providerCancelled")
                : t("auth.providerFailed"),
          );
        }
      } finally {
        // Always: leaving this true would strand the customer on a spinner.
        if (live) setReturning(false);
      }
    })();
    return () => {
      live = false;
    };
  }, [returning, setSession]);

  const goToProvider = useCallback(async (provider: OAuthProvider) => {
    setAuthError(null);
    try {
      await beginOAuth(AUTH_URL, provider, window.location.origin + window.location.pathname);
    } catch {
      setAuthError(t("auth.providerFailed"));
    }
  }, []);

  const goTab = useCallback(
    (next: Tab) => {
      const order = TABS.map((x) => x.key);
      setDir(order.indexOf(next) >= order.indexOf(tab) ? "forward" : "back");
      setTab(next);
      setDetail(null);
      window.scrollTo(0, 0);
    },
    [tab],
  );
  const openDetail = useCallback((d: Detail) => {
    setDir(d ? "forward" : "back");
    setDetail(d);
    window.scrollTo(0, 0);
  }, []);

  // --- signed out ----------------------------------------------------------

  if (!session && returning) {
    return (
      <div className="app">
        <main className="auth">
          <p className="muted">{t("common.loading")}</p>
        </main>
      </div>
    );
  }

  if (!session && erased) {
    return (
      <div className="app">
        <main className="auth">
          <Brand />
          <div>
            <h1 className="display">{t("erase.done")}</h1>
            <p className="lead">{t("erase.doneBody")}</p>
          </div>
          <button type="button" className="btn btn-primary" onClick={() => setErased(false)}>
            {t("common.close")}
          </button>
        </main>
      </div>
    );
  }

  if (!session && !chose) {
    return (
      <div className="app">
        <Intro />
        <Welcome
          mode={authMode}
          onMode={setAuthMode}
          error={authError}
          methods={SIGN_IN_METHODS.filter((id) => {
            if (id === "phone" || id === "email") return true;
            if (!liveProviders) return false;
            return liveProviders.has(id);
          }).map((id) => ({
            id,
            label:
              id === "phone"
                ? t("welcome.continuePhone")
                : id === "email"
                  ? t("welcome.continueEmail")
                  : id === "apple"
                    ? t("auth.withApple")
                    : t("auth.withGoogle"),
          }))}
          onChoose={(id) => {
            setAuthError(null);
            if (id === "apple") void goToProvider("apple");
            else if (id === "google") void goToProvider("google");
            else setChose(id);
          }}
        />
      </div>
    );
  }

  if (!session) {
    return (
      <div className="app">
        <SignIn
          auth={auth}
          mode={chose === "email" ? "email" : "phone"}
          onBack={() => setChose(null)}
          onSignedIn={(s) => {
            setSession(s);
            setAsked(false);
            setOfferPasskey(true);
          }}
        />
      </div>
    );
  }

  // Straight after a code, before the wallet: the moment "next time, no code"
  // means something. Only where this device can make one.
  if (offerPasskey && canPasskey && !hasPasskey) {
    return (
      <div className="app">
        <UsePasskey
          onEnrol={async () => {
            await enrolPasskeyNow();
            say(t("toast.passkeyOn"));
          }}
          onSkip={() => setOfferPasskey(false)}
        />
      </div>
    );
  }

  // --- signed in -------------------------------------------------------------

  const all = cards ?? [];
  const live = all.filter((c) => !c.archived_at);
  const nearlyThere = live.some(
    (c) => c.threshold > 0 && c.balance < c.threshold && c.threshold - c.balance <= NEARLY_THERE,
  );
  const openCard = detail?.kind === "card" ? all.find((c) => c.membership_id === detail.id) : undefined;
  const redeemCard = sheet?.kind === "redeem" ? all.find((c) => c.membership_id === sheet.id) : undefined;

  const eyebrow =
    tab === "home"
      ? greeting()
      : tab === "wallet"
        ? t("shell.eyebrow.wallet")
        : tab === "activity"
          ? t("shell.eyebrow.activity")
          : tab === "profile"
            ? t("sheet.account")
            : t("shell.eyebrow.settings");
  const title =
    tab === "home"
      ? displayName ?? t("shell.title.home")
      : tab === "wallet"
        ? t("wallet.title")
        : tab === "activity"
          ? t("activity.title")
          : tab === "profile"
            ? t("shell.tab.profile")
            : t("shell.tab.settings");
  const tabLabel = (k: Tab) =>
    k === "home"
      ? t("shell.tab.home")
      : k === "wallet"
        ? t("wallet.title")
        : k === "activity"
          ? t("activity.title")
          : k === "profile"
            ? t("shell.tab.profile")
            : t("shell.tab.settings");

  let body: JSX.Element;
  if (detail?.kind === "archive") {
    body = <Archive cards={all.filter((c) => c.archived_at)} onBack={() => openDetail(null)} />;
  } else if (openCard) {
    body = (
      <CardDetail
        card={openCard}
        secret={secret}
        landed={landed && landed.id === openCard.membership_id ? landed.index : null}
        onBack={() => openDetail(null)}
        onRedeem={() => setSheet({ kind: "redeem", id: openCard.membership_id })}
      />
    );
  } else if (tab === "home") {
    body = (
      <Home
        secret={secret}
        cards={all}
        entries={entries}
        favourites={favourites.ids}
        onToggleFavourite={(card) => {
          const on = favourites.toggle(card.membership_id);
          say(t(on ? "favs.saved" : "favs.removed", "en", { name: card.business_name }));
        }}
        setup={{
          hasName: Boolean(displayName),
          canPasskey,
          hasPasskey,
          hasEmail: Boolean(email),
          askEmail: nearlyThere,
        }}
        onOpenCard={(id) => openDetail({ kind: "card", id })}
        onGoWallet={() => goTab("wallet")}
        onGoActivity={() => goTab("activity")}
        onAddName={() => setSheet({ kind: "name" })}
        onUsePasskey={() => setOfferPasskey(true)}
        onAddEmail={() => setSheet({ kind: "email" })}
      />
    );
  } else if (tab === "wallet") {
    body = (
      <Wallet
        view={walletView}
        onView={setWalletView}
        cards={cards}
        asked={asked}
        favourites={favourites.ids}
        onToggleFavourite={(card) => {
          const on = favourites.toggle(card.membership_id);
          say(t(on ? "favs.saved" : "favs.removed", "en", { name: card.business_name }));
        }}
        onOpenCard={(id) => openDetail({ kind: "card", id })}
        onOpenArchive={() => openDetail({ kind: "archive" })}
        onShowCode={() => setSheet({ kind: "code" })}
        onRefresh={() => void refreshNow()}
        refreshing={refreshing}
      />
    );
  } else if (tab === "activity") {
    body = <Activity entries={entries} failed={feedFailed} onRetry={() => void loadFeed()} />;
  } else if (tab === "profile") {
    body = (
      <Profile
        name={displayName}
        phone={phone}
        email={email}
        cards={all}
        onGoWallet={() => goTab("wallet")}
        onGoActivity={() => goTab("activity")}
      />
    );
  } else {
    body = (
      <Settings
        name={displayName}
        phone={phone}
        email={email}
        emailVerified={emailVerified}
        hasPasskey={hasPasskey}
        canPasskey={canPasskey}
        onEditName={() => setSheet({ kind: "name" })}
        onEditEmail={() => setSheet({ kind: "email" })}
        onUsePasskey={() => setOfferPasskey(true)}
        onWhoSees={() => setSheet({ kind: "who" })}
        onExport={() => setSheet({ kind: "export" })}
        onErase={() => setSheet({ kind: "erase" })}
        onSignOut={() => setSheet({ kind: "signout" })}
      />
    );
  }

  const notices = noticesFor(all);
  const unread = notices.filter((n) => !seen.includes(n.id));
  const markRead = () => {
    const ids = notices.map((n) => n.id);
    setSeen(ids);
    writeSeen(ids);
    setSheet(null);
  };

  const closeSheet = () => setSheet(null);
  const tabIndex = TABS.findIndex((x) => x.key === tab);

  return (
    <div className="app app-shell">
      <header className="bar">
        <div className="shrink">
          <p className="bar-eyebrow">{eyebrow}</p>
          <h1 className="bar-title">{title}</h1>
        </div>
        {/* The artboard's "Updates" and "+". Updates is here with what the phone
            worked out; the "+" is not, because there is no customer-side join
            route, and its place is taken by the code, which is the thing a
            customer needs from any screen. Home already shows the code. */}
        <div className="bar-actions">
          <button
            type="button"
            className="updates-btn"
            data-unread={unread.length ? "" : undefined}
            onClick={() => setSheet({ kind: "updates" })}
          >
            {t("shell.updates")}
            {unread.length > 0 && <span className="updates-count">{unread.length}</span>}
          </button>
          {(tab !== "home" || detail) && (
            <button type="button" className="code-btn" onClick={() => setSheet({ kind: "code" })}>
              {t("shell.code")}
            </button>
          )}
        </div>
      </header>

      {problem === "offline" && (
        <p className="banner" role="status">
          {t("error.offline.title")}. {t("code.works_offline")}
        </p>
      )}
      {problem === "server" && (
        <p className="banner" data-bad="" role="status">
          {t("error.generic.title")}
        </p>
      )}

      <main className="main">
        <div
          className="screen"
          data-screen={detail ? detail.kind : tab}
          data-dir={dir === "back" ? "back" : undefined}
          key={`${tab}:${detail ? (detail.kind === "card" ? detail.id : "archive") : ""}`}
        >
          {body}
        </div>
      </main>

      <nav className="tabs" aria-label="Sections" style={{ ["--i" as string]: String(tabIndex) }}>
        <span className="tabs-rail" aria-hidden="true" />
        {TABS.map((x) => (
          <button
            key={x.key}
            type="button"
            className="tab"
            aria-current={tab === x.key ? "page" : undefined}
            onClick={() => (tab === x.key && detail ? openDetail(null) : goTab(x.key))}
          >
            <Icon name={x.icon} />
            {tabLabel(x.key)}
          </button>
        ))}
      </nav>

      {sheet?.kind === "code" && <CodeSheet secret={secret} onClose={closeSheet} />}
      {sheet?.kind === "redeem" && redeemCard && <RedeemSheet card={redeemCard} secret={secret} onClose={closeSheet} />}
      {sheet?.kind === "name" && <NameSheet initial={displayName} onSave={saveName} onClose={closeSheet} />}
      {sheet?.kind === "email" && <EmailSheet initial={email} onSave={saveEmail} onClose={closeSheet} />}
      {sheet?.kind === "who" && <WhoSeesSheet onClose={closeSheet} />}
      {sheet?.kind === "updates" && <UpdatesSheet notices={notices} onMarkRead={markRead} onClose={closeSheet} />}
      {sheet?.kind === "export" && <ExportSheet onDownload={downloadData} onClose={closeSheet} />}
      {sheet?.kind === "signout" && (
        <SignOutSheet
          cards={live.length}
          who={phone ? phoneForDisplay(phone) : email || ""}
          hasPasskey={hasPasskey}
          onConfirm={signOut}
          onClose={closeSheet}
        />
      )}
      {sheet?.kind === "erase" && <EraseSheet cards={all} onConfirm={erase} onClose={closeSheet} />}

      {seal && <Seal label={seal} />}
      {toast && <Toast message={toast.message} stamp={toast.stamp} />}
    </div>
  );
}
